import * as api from '../api.js';

import { CHECKOUT_STAGES, COUNTRY_CURRENCY_MAP } from '#constants';
import { metaCommerceParams } from './tracking.action.js';
import rollbar from '#services/rollbar';

export const reviewOrder = async (set, get) => {
  // Determine if we need to go to the payment stage or not
  const sub = get().sub;
  if (sub?._id) {
    // We have an active sub, create the tracker.
    const result = await get().createTracker({ subId: sub._id });
    if (result?.failure) {
      return;
    }
    await get().setCheckoutStage(CHECKOUT_STAGES[2]);
    return;
  }
  // See if a discount code is applied
  if (get().code === 'YEAROFF') {
    return get().codeCheckout();
  }
  get().setCheckoutStage(CHECKOUT_STAGES[1]);
  return {};
};

export const getPromotion = async ({ promotionCode }, set, get) => {
  get().setPromotion({
    hasFetched: false,
  });
  const { success, failure } = await api.getPromotion({
    promotionCode,
  });
  if (failure) {
    get().setToast({
      title: 'Promotion Failed',
      description: failure.message,
      status: 'error',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
    return { failure: true };
  }
  const { isValid, code } = success;
  if (isValid) {
    get().setPromotion({
      code: code[0],
      isValid: true,
      hasFetched: true,
    });
  } else {
    get().setPromotion({
      isValid: false,
      hasFetched: true,
    });
  }
  return { success, failure };
};

export const codeCheckout = async (set, get) => {
  // We already have the subscription. Just create the tracker
  const code = get().code;
  const { success, failure } = await api.createCodeSub({
    code,
    planSlug: get().planSlug,
  });
  if (failure) {
    set({ loading: false });
    get().setToast({
      title: "Couldn't start subscription",
      description: failure.message,
      status: 'error',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
    // end loading
    return { failure: true };
  }

  const { subId } = success;
  // Step 1: Update the subscription to active
  await get().updateSubStatus({
    status: 'activate',
    _id: subId,
    paymentId: null,
    customPayment: {
      id: code,
      status: 'complete',
      amount: 0,
      currency: 'USD',
    },
  });

  get().setCheckoutStage(CHECKOUT_STAGES[2]);
  get().updateUser({ calle: 'codeCheckout' });
  return { success, failure };
};

export const createPaymentIntent = async (set, get) => {
  // Check if we actually need a intent
  if (get().sub?._id) {
    // We already have a subscription.
    // No need to get intent
    return { success: { data: 'Existing subscription' } };
  }
  if (get().code === 'YEAROFF') {
    // We shouldn't get intent
    // when we have a code.
    return { success: { data: 'Influencer signup' } };
  }
  const planSlug = get().planSlug;
  if (!planSlug) {
    // No plan selected yet (e.g. selection was cleared after login). Don't
    // fire a payment intent we know will fail; wait until a plan is set.
    return { failure: true };
  }
  let location = get().location;
  if (location.updated) {
    // We haven't fetched the current location wait for that to happen
    location = await get().getCurrentLocation();
  }
  const currency = COUNTRY_CURRENCY_MAP[location.country];

  const requestData = {
    planSlug,
    currency,
    promotionCode: get().promotion?.code?.code,
  };

  let result;
  try {
    result = await api.createPaymentIntent(requestData);
  } catch (error) {
    // Log API call exception
    rollbar.error('createPaymentIntent API call failed with exception', {
      error: error.message,
      errorStack: error.stack,
      requestData,
      userId: get().user?._id,
      userEmail: get().user?.email,
      location: location?.country,
    });
    return { failure: { message: 'Payment intent creation failed', error } };
  }

  const { success, failure } = result;

  if (failure) {
    // Log API failure response
    rollbar.error('createPaymentIntent API returned failure', {
      failure,
      requestData,
      userId: get().user?._id,
      userEmail: get().user?.email,
      location: location?.country,
    });
    return { failure: true };
  }

  get().setStripePayments({
    clientSecret: success.clientSecret,
    subId: success.subId,
    paymentId: success.paymentId,
  });
  return { success, failure };
};

export const removePaymentIntent = async (set, get) => {
  get().setStripePayments({
    clientSecret: null,
    subId: null,
    paymentId: null,
  });
  return { success: true };
};

export const onPaymentSuccess = async (set, get, { subId, paymentId }) => {
  // This is the place to setup the tracker.
  get().setLoadingMessage('Activating your subscription...');

  // A 3DS redirect reloads checkout before `plans` is fetched again.
  // Wait for that fetch so purchase analytics still run, then always
  // continue to activation. The charge has already succeeded.
  try {
    let plan = get().plans.find((p) => p.slug === get().planSlug);
    if (!plan && get().planSlug) {
      await get().getPlans();
      plan = get().plans.find((p) => p.slug === get().planSlug);
    }
    if (!plan) {
      throw new Error('Plan unavailable for purchase analytics');
    }
    // Get the currency of the user
    const currency = COUNTRY_CURRENCY_MAP[get().location.country];
    const checkoutCost = get().getCheckoutCost();
    window.gtag('event', 'purchase', {
      transaction_id: subId,
      value: checkoutCost,
      currency,
      coupon: get().promotion?.code?.code ?? '',
      items: [
        {
          item_id: plan._id,
          item_name: plan.name,
          location_id: 'ChIJIQBpAG2ahYAR_6128GcTUEo',
          price: checkoutCost,
          quantity: 1,
        },
      ],
    });
    const purchase = metaCommerceParams(get, { charged: true });
    get().sendFBEvent({
      eventName: 'Purchase',
      params: purchase,
    });
    if (get().planSlug !== 'pay-as-you-go') {
      get().sendFBEvent({
        eventName: 'Subscribe',
        params: purchase,
      });
    }
  } catch (e) {
    rollbar.error(e);
  }
  // Step 1: Update the subscription to active. Can't create a tracker on inactive sub
  await get().updateSubStatus({
    status: 'activate',
    _id: subId,
    paymentId,
  });

  // Step 2: Fetch the new subscriptions
  await get().getSub();

  // Step 3: Create the tracker
  get().setLoadingMessage('Setting up your tracker...');
  await get().createTracker({ subId });

  // Step 4: Navigate to success
  get().setCheckoutStage(CHECKOUT_STAGES[2]);
};
